import * as j from '@mojotech/json-type-validation';

// Preview campaign settings pushPreviewSeconds property defaults and ranges
export const AsyncConcurrency = {
  Min: 1,
  Max: 10,
  Default: 5,
} as const;

export interface Agent {
  username: string;
  fullName: string;
  firstName: string;
  lastName: string;
  diallerGroupId: number | null;
  diallerGroupName: string | null;
  routingProfileId: string | null;
  disabled: boolean;
  skills: string[];
  asyncQueues: string[];
  asyncMaxConcurrency: number;
  asyncDesiredConcurrency: number;
}

export type AgentListItem = Omit<Agent, 'asyncMaxConcurrency' | 'asyncDesiredConcurrency'>;

/**
 * AgentUpdateRequest is used when adding / updating Agent records in the backend
 */
export type AgentUpdateRequest = Omit<Agent, 'fullName' | 'diallerGroupName' | 'disabled'> & {
  accessFilterIds: number[] | undefined;
};

export interface SkillType {
  skillType: string;
  description: string;
}

export interface ExclusionList {
  name: string;
  description: string;
  numEntries: string;
}

export interface CreateExclusionList {
  name: string;
  description: string;
}

export interface ExclusionEntry {
  number: string;
  reason: string;
  expiryTimestamp?: string;
  timezone?: string;
}

export interface ExclusionListListedEndpoint {
  isListed: boolean;
  listName: string;
  phone: string;
}

export type BadExclusionRecord = {
  row: number;
  error: string;
};

export interface ExclusionListUploadResponse {
  badRecords: BadExclusionRecord[];
  importErrorCount: number;
  validCount: number;
}

export interface RecordingInfo {
  durationInSeconds: number;
  date: string;
  recordingId: string;
}

export interface DispositionAttribute {
  attribute: string;
  values: string[];
}

export interface PredictiveAgentStatus {
  agentStatus: string;
  description: string;
  position: number;
}

export enum PollyLanguage {
  ARB = 'arb',
  CMN_CN = 'cmn-CN',
  CY_GB = 'cy-GB',
  DA_DK = 'da-DK',
  DE_DE = 'de-DE',
  EN_AU = 'en-AU',
  EN_GB = 'en-GB',
  EN_GB_WLS = 'en-GB-WLS',
  EN_IN = 'en-IN',
  EN_US = 'en-US',
  ES_ES = 'es-ES',
  ES_MX = 'es-MX',
  ES_US = 'es-US',
  FR_CA = 'fr-CA',
  FR_FR = 'fr-FR',
  FR_BE = 'fr-BE',
  IS = 'is-IS',
  IT = 'it-IT',
  JA = 'ja-JP',
  HI = 'hi-IN',
  KO = 'ko-KR',
  NB = 'nb-NO',
  NL = 'nl-NL',
  PL = 'pl-PL',
  PT_BR = 'pt-BR',
  PT_PT = 'pt-PT',
  RO = 'ro-RO',
  RU = 'ru-RU',
  SV = 'sv-SE',
  TR = 'tr-TR',
  EN_NZ = 'en-NZ',
  EN_ZA = 'en-ZA',
  CA_ES = 'ca-ES',
  DE = 'de-AT',
  YUE = 'yue-CN',
  AR_AE = 'ar-AE',
  FI = 'fi-FI',
  EN_IE = 'en-IE',
  NL_BE = 'nl-BE',
}

export interface PredictiveIvrMessage {
  key: string;
  description: string;
  content?: string;
  filename?: string; // auto-generated field at backend
  audioLocation?: string;
  signedUrl?: string;
  language?: PollyLanguage | null;
  voice?: string | null;
  file?: File; // for uploading only
}

export interface AccessFilterAgentEntry {
  id: string;
  name: string;
  disabled: string | undefined;
}

export interface AccessFilterDiallerGroupEntry {
  id: number;
  name: string;
  archived: string | undefined;
}

export interface AccessFilterCampaignEntry {
  id: number;
  name: string;
  archived: string | undefined;
}

export interface AccessFilterAsyncQueueEntry {
  id: string;
  name: string;
}

export interface AccessFilter {
  id: number;
  name: string;
  description: string;
  archived: string | undefined;
  numAgents: number | undefined;
  numCampaigns: number | undefined;
  numGroups: number | undefined;
  numAsyncQueues: number | undefined;
  agents: AccessFilterAgentEntry[] | undefined;
  groups: AccessFilterDiallerGroupEntry[] | undefined;
  campaigns: AccessFilterCampaignEntry[] | undefined;
  asyncQueues: AccessFilterAsyncQueueEntry[] | undefined;
}

export type CreateAccessFilter = Pick<AccessFilter, 'name' | 'description'>;
export interface UpdateAccessFilter {
  name: string;
  description: string;
  agents: string[];
  groups: number[];
  campaigns: number[];
  asyncQueues: string[];
}

export interface SipQueue {
  queueID: number;
  queueName: string;
  description: string;
}

export type NewSipQueue = Omit<SipQueue, 'queueID'>;

const GetSkillTypeResponseDecoder: j.Decoder<SkillType> = j
  .object({
    skill_type: j.string(),
    description: j.string(),
  })
  .map((item: any) => ({
    skillType: item.skill_type,
    description: item.description,
  }));

export const GetSkillTypesResponseDecoder: j.Decoder<SkillType[]> = j
  .object({
    skill_types: j.array(GetSkillTypeResponseDecoder),
  })
  .map((item: any) => item.skill_types);

const GetExclusionListResponseDecoder: j.Decoder<ExclusionList> = j
  .object({
    name: j.string(),
    description: j.string(),
    num_entries: j.number(),
  })
  .map((item: any) => ({
    name: item.name,
    description: item.description,
    numEntries: item.num_entries,
  }));

export const GetExclusionListsResponseDecoder: j.Decoder<ExclusionList[]> = j
  .object({
    exclusion_lists: j.array(GetExclusionListResponseDecoder),
  })
  .map((item: any) => item.exclusion_lists);

export const GetExclusionListListedEndpointResponseDecoder: j.Decoder<ExclusionListListedEndpoint> = j
  .object({
    is_listed: j.boolean(),
    list_name: j.string(),
    phone: j.string(),
  })
  .map((item: any) => ({
    isListed: item.is_listed,
    listName: item.list_name,
    phone: item.phone,
  }));

const BadExclusionRecord: j.Decoder<BadExclusionRecord> = j
  .object({
    row: j.number(),
    error: j.string(),
  })
  .map((item) => ({
    row: item.row,
    error: item.error,
  }));

export const GetExclusionListUploadResponseDecoder: j.Decoder<ExclusionListUploadResponse> = j
  .object({
    bad_records: j.array(BadExclusionRecord),
    import_error_count: j.number(),
    valid_count: j.number(),
  })
  .map((item: any) => ({
    badRecords: item.bad_records,
    importErrorCount: item.import_error_count,
    validCount: item.valid_count,
  }));

const GetRecordingInfoItemResponseDecoder: j.Decoder<RecordingInfo> = j
  .object({
    duration_in_seconds: j.number(),
    date: j.string(),
    recording_id: j.string(),
  })
  .map((item: any) => ({
    durationInSeconds: item.duration_in_seconds,
    date: item.date,
    recordingId: item.recording_id,
  }));

export const GetRecordingInfoListResponseDecoder: j.Decoder<RecordingInfo[]> = j
  .object({
    recording_info_list: j.array(GetRecordingInfoItemResponseDecoder),
  })
  .map((item: any) => item.recording_info_list);

const GetDispositionAttributeResponseDecoder: j.Decoder<DispositionAttribute> = j
  .object({
    attribute: j.string(),
    values: j.array(j.string()),
  })
  .map((item: any) => ({
    attribute: item.attribute,
    values: item.values,
  }));

export const GetDispositionAttributesResponseDecoder: j.Decoder<DispositionAttribute[]> = j
  .object({
    attributes: j.union(j.array(GetDispositionAttributeResponseDecoder), j.constant(null)),
  })
  .map((item: any) => item.attributes || []);

const GetPredictiveIvrMessageResponseDecoder: j.Decoder<PredictiveIvrMessage> = j
  .object({
    key: j.string(),
    content: j.union(j.string(), j.constant(null)),
    description: j.string(),
    filename: j.string(),
    audio_location: j.string(),
    signed_url: j.string(),
    polly_language: j.union(j.string(), j.constant(null)),
    polly_voice: j.union(j.string(), j.constant(null)),
  })
  .map((item: any) => ({
    key: item.key,
    content: item.content || undefined,
    description: item.description,
    filename: item.filename,
    audioLocation: item.audio_location,
    signedUrl: item.signed_url,
    language: item.polly_language || undefined,
    voice: item.polly_voice || undefined,
  }));
export const GetPredictiveIvrMessagesResponseDecoder: j.Decoder<PredictiveIvrMessage[]> = j
  .object({
    messages: j.union(j.array(GetPredictiveIvrMessageResponseDecoder), j.constant(null)),
  })
  .map((item: any) => item.messages || []);

const GetPredictiveAgentStatusListDecoder: j.Decoder<PredictiveAgentStatus> = j
  .object({
    agent_status: j.string(),
    description: j.string(),
    position: j.number(),
  })
  .map(
    (item): PredictiveAgentStatus => ({
      agentStatus: item.agent_status,
      description: item.description,
      position: item.position,
    }),
  );

export const GetPredictiveAgentStatusesDecoder: j.Decoder<PredictiveAgentStatus[]> = j
  .object({
    statuses: j.union(j.array(GetPredictiveAgentStatusListDecoder), j.constant(null)),
  })
  .map((resp) => resp.statuses ?? []);

const GetSipQueueResponseDecoder: j.Decoder<SipQueue> = j
  .object({
    queue_id: j.number(),
    queue_name: j.string(),
    description: j.string(),
  })
  .map((item: any) => ({
    queueID: item.queue_id,
    queueName: item.queue_name,
    description: item.description,
  }));

export const GetSipQueuesResponseDecoder: j.Decoder<SipQueue[]> = j
  .object({
    queues: j.array(GetSipQueueResponseDecoder),
  })
  .map((item: any) => item.queues);
